import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  makeStyles, Button, Typography, Container, Paper,
  Select, MenuItem, FormControl, InputLabel, Grid,
  Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, Chip, CircularProgress, Divider
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import LinkIcon from "@material-ui/icons/Link";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: { padding: theme.spacing(4) },
  paper: { padding: theme.spacing(3), marginBottom: theme.spacing(3) },
  title: { marginBottom: theme.spacing(2), fontWeight: 700 },
  btn: { marginTop: theme.spacing(2) },
  chip: { marginRight: 4 },
  section: { marginTop: theme.spacing(3) },
  formControl: { minWidth: 200, marginRight: theme.spacing(2), marginBottom: theme.spacing(2) },
  divider: { margin: theme.spacing(3, 0) },
}));

const FacebookLeads = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const [loading, setLoading] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [pages, setPages] = useState([]);
  const [forms, setForms] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [selectedTag, setSelectedTag] = useState("");
  const [formFields, setFormFields] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({ name: "", phone: "", email: "" });
  const [userToken, setUserToken] = useState("");
  const [step, setStep] = useState("list");

  useEffect(() => {
    loadIntegrations();
    loadTags();

    const errorParam = params.get("error");
    if (errorParam) {
      toast.error(decodeURIComponent(errorParam));
      history.replace("/facebook-leads");
      return;
    }

    const pagesParam = params.get("pages");
    const userTokenParam = params.get("userToken");
    if (pagesParam && userTokenParam) {
      try {
        const parsed = JSON.parse(decodeURIComponent(pagesParam));
        if (!Array.isArray(parsed) || parsed.length === 0) {
          toast.error("Nenhuma página encontrada. Certifique-se de que você é ADMINISTRADOR da página no Facebook e tente novamente.");
          history.replace("/facebook-leads");
          return;
        }
        setPages(parsed);
        setUserToken(userTokenParam);
        setStep("selectPage");
      } catch (e) {
        toast.error("Erro ao processar dados do Facebook. Tente novamente.");
        history.replace("/facebook-leads");
      }
    }
  }, []);

  const loadIntegrations = async () => {
    try {
      const { data } = await api.get("/facebook-leads");
      setIntegrations(data);
    } catch (err) { toastError(err); }
  };

  const loadTags = async () => {
    try {
      const { data } = await api.get("/tag/kanban");
      setTags(data.lista || []);
    } catch (err) { toastError(err); }
  };

  const connectFacebook = async () => {
    try {
      const { data } = await api.get("/facebook-leads/auth");
      window.location.href = data.url;
    } catch (err) { toastError(err); }
  };

  const handleSelectPage = async (page) => {
    setSelectedPage(page);
    setLoading(true);
    try {
      const { data } = await api.get("/facebook-leads/forms", {
        params: { pageId: page.id, pageAccessToken: page.access_token }
      });
      if (!Array.isArray(data) || data.length === 0) {
        toast.warning("Nenhum formulário de lead ativo encontrado nesta página. Crie um formulário no Gerenciador de Anúncios do Facebook e tente novamente.");
        setLoading(false);
        return;
      }
      setForms(data);
      setStep("selectForm");
    } catch (err) { toastError(err); }
    setLoading(false);
  };

  const handleSelectForm = (form) => {
    setSelectedForm(form);
    const fields = (form.questions || []).map(q => q.key);
    setFormFields(fields);
    setStep("mapFields");
  };

  const handleSave = async () => {
    if (!selectedPage || !selectedForm) {
      toast.error("Selecione a página e o formulário");
      return;
    }
    setLoading(true);
    try {
      await api.post("/facebook-leads/save", {
        pageId: selectedPage.id,
        pageName: selectedPage.name,
        pageAccessToken: selectedPage.access_token,
        formId: selectedForm.id,
        formName: selectedForm.name,
        tagId: selectedTag,
        fieldMapping
      });
      toast.success("Integração salva com sucesso!");
      setStep("list");
      loadIntegrations();
    } catch (err) { toastError(err); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete("/facebook-leads/" + id);
      toast.success("Integração removida");
      loadIntegrations();
    } catch (err) { toastError(err); }
  };

  return (
    <Container maxWidth="lg" className={classes.root}>
      <Typography variant="h5" className={classes.title}>Facebook Lead Ads</Typography>

      {step === "list" && (
        <>
          <Paper className={classes.paper}>
            <Typography variant="body1" gutterBottom>
              Conecte sua página do Facebook para receber leads automaticamente no Kanban.
            </Typography>
            <Button variant="contained" color="primary" startIcon={<LinkIcon />}
              onClick={connectFacebook} className={classes.btn}>
              Conectar página do Facebook
            </Button>
          </Paper>

          {integrations.length > 0 && (
            <Paper className={classes.paper}>
              <Typography variant="h6" gutterBottom>Integrações ativas</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Página</TableCell>
                    <TableCell>Formulário</TableCell>
                    <TableCell>Coluna Kanban</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {integrations.map(i => (
                    <TableRow key={i.id}>
                      <TableCell>{i.pageName}</TableCell>
                      <TableCell>{i.formName}</TableCell>
                      <TableCell>
                        {tags.find(t => t.id === i.tagId)?.name || i.tagId || "Em aberto"}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={i.isActive ? "Ativo" : "Inativo"}
                          color={i.isActive ? "primary" : "default"} />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleDelete(i.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}

      {step === "selectPage" && (
        <Paper className={classes.paper}>
          <Typography variant="h6" gutterBottom>Selecione a página</Typography>
          <Grid container spacing={2}>
            {pages.map(page => (
              <Grid item xs={12} sm={6} md={4} key={page.id}>
                <Button variant="outlined" fullWidth onClick={() => handleSelectPage(page)}
                  disabled={loading}>
                  {page.name}
                </Button>
              </Grid>
            ))}
          </Grid>
          {loading && <CircularProgress size={24} style={{ marginTop: 16 }} />}
        </Paper>
      )}

      {step === "selectForm" && (
        <Paper className={classes.paper}>
          <Typography variant="h6" gutterBottom>
            Página: <strong>{selectedPage?.name}</strong> — Selecione o formulário
          </Typography>
          <Grid container spacing={2}>
            {forms.map(form => (
              <Grid item xs={12} sm={6} key={form.id}>
                <Button variant="outlined" fullWidth onClick={() => handleSelectForm(form)}>
                  {form.name}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {step === "mapFields" && (
        <Paper className={classes.paper}>
          <Typography variant="h6" gutterBottom>
            Formulário: <strong>{selectedForm?.name}</strong>
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Mapeie os campos do formulário com os campos do contato
          </Typography>
          <Divider className={classes.divider} />
          <Grid container spacing={2}>
            {["name", "phone", "email"].map(field => (
              <Grid item xs={12} sm={4} key={field}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>{field === "name" ? "Nome" : field === "phone" ? "Telefone" : "E-mail"}</InputLabel>
                  <Select
                    value={fieldMapping[field]}
                    onChange={e => setFieldMapping({ ...fieldMapping, [field]: e.target.value })}
                    label={field === "name" ? "Nome" : field === "phone" ? "Telefone" : "E-mail"}
                  >
                    <MenuItem value=""><em>Não mapear</em></MenuItem>
                    {formFields.map(f => (
                      <MenuItem key={f} value={f}>{f}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ))}
          </Grid>
          <Divider className={classes.divider} />
          <Typography variant="body2" gutterBottom>Coluna do Kanban onde os leads vão entrar:</Typography>
          <FormControl variant="outlined" size="small" className={classes.formControl}>
            <InputLabel shrink>Coluna do Kanban</InputLabel>
            <Select value={selectedTag} onChange={e => setSelectedTag(e.target.value)} label="Coluna do Kanban" displayEmpty notched>
              <MenuItem value=""><em>Em aberto (padrão)</em></MenuItem>
              {tags.map(tag => (
                <MenuItem key={tag.id} value={tag.id}>{tag.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <br />
          <Button variant="contained" color="primary" onClick={handleSave}
            disabled={loading} className={classes.btn}>
            {loading ? <CircularProgress size={20} /> : "Salvar integração"}
          </Button>
        </Paper>
      )}
    </Container>
  );
};

export default FacebookLeads;
